import { useState } from "react";
import {
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { Add, Edit, MoreVert, ToggleOff, ToggleOn } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import { useSnackbar } from "notistack";
import { useAppSelector } from "@/redux/hooks";
import { useAuth } from "../../../contexts/authContext";
import CardBase from "../../../components/cards/CardBase";
import useBranding from "../../hooks/queries/useBranding";
import useSaveBranding from "../../hooks/mutations/useSaveBranding";
import BrandingEditorDialog from "./BrandingEditorDialog";
import BrandingLogo from "./BrandingLogo";
import type { BrandingDTO, SaveBrandingRequest } from "../../types/brandingDtos";

const useStyles = makeStyles((theme) => ({
    empty: { color: theme.palette.text.secondary, padding: theme.spacing(3), textAlign: "center" },
    colours: { alignItems: "center", display: "flex", gap: theme.spacing(1) },
    swatch: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        height: 20,
        width: 20,
    },
    defaultSwatch: {
        border: `1px dashed ${theme.palette.text.secondary}`,
        borderRadius: theme.shape.borderRadius,
        height: 20,
        opacity: 0.55,
        width: 20,
    },
    coverage: {
        color: theme.palette.text.secondary,
        display: "block",
        marginTop: theme.spacing(0.5),
    },
    menuItem: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: "flex",
        gap: theme.spacing(1),
    },
}));

export default function BrandingTableCard() {
    const classes = useStyles();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { sites, selectedAccount } = useAppSelector((state: any) => state.account);
    const { isSafetyNestAdministratorForSites, isTemplateAdminForSites, isWorknestUser } = useAuth();

    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<BrandingDTO | null>(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openMenu, setOpenMenu] = useState<number | false>(false);

    const handleClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setOpenMenu(id);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setOpenMenu(false);
    };

    const accountExternalId = selectedAccount?.account?.externalId;
    const siteOptions = Object.values(sites ?? {});

    // Passing one site id at a time turns the account-wide role helpers into per-site
    // checks, which is what branding needs — the API gates each scope separately.
    const canManageSite = (siteExternalId: string) =>
        isWorknestUser ||
        isTemplateAdminForSites([siteExternalId]) ||
        isSafetyNestAdministratorForSites([siteExternalId]);

    const accountSiteIds: string[] = selectedAccount?.childExternalIds ?? [];
    const canManageAllSites =
        isWorknestUser ||
        (accountSiteIds.length > 0 && accountSiteIds.every((id) => isSafetyNestAdministratorForSites([id])));

    const manageableSites = siteOptions.filter((site: any) => canManageSite(site.externalId));
    const canManage = (row: BrandingDTO) =>
        row.siteExternalId ? canManageSite(row.siteExternalId) : canManageAllSites;
    const canAddBranding = canManageAllSites || manageableSites.length > 0;

    const branding = useBranding({ accountExternalId });
    const saveBranding = useSaveBranding();

    const scopeLabel = (row: BrandingDTO) =>
        !row.siteExternalId
            ? "All sites without their own branding"
            : sites?.[row.siteExternalId]?.name ?? "Unknown site";

    const unordered: BrandingDTO[] = Array.isArray(branding?.data) ? branding.data : [];
    const takenScopes = unordered.map((row) => row.siteExternalId ?? null);

    // All sites first, then sites alphabetically, so precedence reads top-down.
    const rows = [...unordered].sort((a, b) => {
        if (!a.siteExternalId) return -1;
        if (!b.siteExternalId) return 1;
        return scopeLabel(a).localeCompare(scopeLabel(b));
    });

    const accountWideIsActive = rows.some((row) => !row.siteExternalId && row.isActive);

    // A site row counts as orphaned when the account no longer has that site.
    const isOrphanedSiteRow = (row: BrandingDTO) => !!row.siteExternalId && row.coveredSiteCount === 0;

    // "On" alone is misleading: a row can be switched on and still reach nothing.
    const status = (row: BrandingDTO) => {
        if (!row.isActive) return { label: "Off", color: "default" as const };
        if (!row.isInUse) return { label: "Not in use", color: "warning" as const };

        return { label: "On", color: "primary" as const };
    };

    // The account-wide row only reaches sites that don't override it, so spell that out
    // rather than leaving two "On" rows looking like a conflict.
    const coverage = (row: BrandingDTO) => {
        if (isOrphanedSiteRow(row)) return "This site is no longer on the account";

        if (row.siteExternalId)
            return row.isActive
                ? accountWideIsActive
                    ? "Overrides the all-sites branding"
                    : null
                : accountWideIsActive
                  ? "Uses the all-sites branding instead"
                  : "Uses the standard WorkNest look";

        if (!row.isActive) return "Sites without their own branding use the standard WorkNest look";

        if (row.totalSiteCount === 0) return null;
        if (row.coveredSiteCount <= 0) return "Every site has its own branding";
        if (row.coveredSiteCount === row.totalSiteCount) return `Used by all ${row.totalSiteCount} sites`;

        return `Used by ${row.coveredSiteCount} of ${row.totalSiteCount} sites`;
    };

    const refresh = () => branding.refetch?.();

    const openCreate = () => {
        setEditing(null);
        setEditorOpen(true);
    };

    const openEdit = (row: BrandingDTO) => {
        setEditing(row);
        setEditorOpen(true);
    };

    const save = (request: SaveBrandingRequest) =>
        saveBranding.mutate(request, {
            onSuccess: () => {
                setEditorOpen(false);
                refresh();
                enqueueSnackbar(editing ? "Branding updated" : "Branding added", { variant: "default" });
            },
            onError: () => enqueueSnackbar("Could not save the branding. Please try again.", { variant: "error" }),
        });

    // Toggling only flips the switch, so the stored logo and colours are left alone.
    const toggleActive = (row: BrandingDTO) =>
        saveBranding.mutate(
            { siteExternalId: row.siteExternalId, primaryColour: row.primaryColour, secondaryColour: row.secondaryColour, isActive: !row.isActive },
            {
                onSuccess: () => refresh(),
                onError: () => enqueueSnackbar("Could not update the branding. Please try again.", { variant: "error" }),
            }
        );

    // A colour left unset falls back to the WorkNest one, so show that rather than a gap.
    const colourSwatches = (row: BrandingDTO) =>
        [
            { label: "Primary", colour: row.primaryColour, fallback: theme.palette.primary.main },
            { label: "Secondary", colour: row.secondaryColour, fallback: theme.palette.secondary.main },
        ].map(({ label, colour, fallback }) => ({
            label,
            colour: colour || fallback,
            isDefault: (colour || fallback).toLowerCase() === fallback.toLowerCase(),
        }));

    return (
        <CardBase
            title="Branding"
            description="Your logo and colours, used on risk assessments in place of the standard WorkNest look. A site with its own branding overrides the all-sites branding."
            isLoading={branding?.isLoading}
            isFetching={branding?.isFetching}
            rightComponent={
                canAddBranding && (
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={openCreate}>
                        Add branding
                    </Button>
                )
            }
        >
            {rows.length === 0 ? (
                <Typography className={classes.empty}>
                    No branding yet. Add your logo and colours to replace the standard WorkNest look.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Applies to</TableCell>
                                <TableCell>Logo</TableCell>
                                <TableCell>Colours</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <Chip size="small" variant="outlined" label={scopeLabel(row)} />
                                        {coverage(row) && (
                                            <Typography variant="caption" className={classes.coverage}>
                                                {coverage(row)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <BrandingLogo reference={row.logoReference} fileName={row.logoFileName} />
                                    </TableCell>
                                    <TableCell>
                                        <div className={classes.colours}>
                                            {colourSwatches(row).map(({ label, colour, isDefault }) => (
                                                <Tooltip
                                                    key={label}
                                                    title={`${label}: ${colour}${isDefault ? " (WorkNest default)" : ""}`}
                                                >
                                                    <div
                                                        className={isDefault ? classes.defaultSwatch : classes.swatch}
                                                        style={{ backgroundColor: colour }}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip
                                            title={
                                                status(row).label === "Not in use"
                                                    ? "Branding is switched on, but nothing currently uses it."
                                                    : ""
                                            }
                                        >
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={status(row).color}
                                                label={status(row).label}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="More">
                                            <IconButton
                                                aria-label="more"
                                                aria-controls="branding-menu"
                                                aria-haspopup="true"
                                                onClick={(e) => handleClick(e, row.id)}
                                            >
                                                <MoreVert />
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            id="branding-menu"
                                            anchorEl={anchorEl}
                                            keepMounted
                                            open={openMenu === row.id}
                                            onClose={handleClose}
                                        >
                                            <MenuItem
                                                className={classes.menuItem}
                                                disabled={!canManage(row)}
                                                onClick={() => {
                                                    handleClose();
                                                    openEdit(row);
                                                }}
                                            >
                                                <Edit />
                                                Edit Branding
                                            </MenuItem>
                                            <MenuItem
                                                className={classes.menuItem}
                                                disabled={!canManage(row)}
                                                onClick={() => {
                                                    handleClose();
                                                    toggleActive(row);
                                                }}
                                            >
                                                {row.isActive ? <ToggleOff /> : <ToggleOn />}
                                                Turn Branding {row.isActive ? "Off" : "On"}
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <BrandingEditorDialog
                open={editorOpen}
                branding={editing}
                sites={manageableSites}
                canManageAllSites={canManageAllSites}
                takenScopes={takenScopes}
                isSaving={saveBranding?.isLoading}
                onClose={() => setEditorOpen(false)}
                onSave={save}
            />
        </CardBase>
    );
}
